// React Dependencies
import React, { useState, useEffect, useRef } from 'react';

// External Dependencies
import moment from 'moment';
import MaterialTable from 'material-table';
import { useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Paper, Typography } from '@material-ui/core';

// Internal Dependencies
import apiService from '../../../services.js/apiService';
import Select from '../../../components/common/filters/SelectFilter';
import InputFilter from '../../../components/common/filters/InputFilter';
import BusinessAccountsTable from './BusinessAccountsTable';
import useDebounce from '../../../components/global/use-debounce';
import { Url, Status, SortType } from '../../../common/constants';

const STATUS_OPTS = [
  {
    label: Status.Pending,
    value: Status.Pending,
  },
  {
    label: Status.Live,
    value: Status.Live,
  },
  {
    label: Status.Rejected,
    value: Status.Rejected,
  },
];

function BusinessClaimTable() {
  const history = useHistory();
  const tableRef = useRef(null);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ status: Status.Pending });
  const [isLoading, setIsLoading] = useState(true);
  const debouncedFilters = useDebounce(filters, 500);

  const handleFilterSelect = (value, name) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value ? value.value : null,
    }));
  };

  const handleFilter = (value, name) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value || null,
    }));
  };

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.onQueryChange();
    }
  }, [debouncedFilters]);

  return (
    <>
      <div className="content-header">
        <h1>Business</h1>
      </div>

      <Paper>
        <div className="w-100 m-2 p-2 d-flex flex-wrap" style={{ gap: 5 }}>
          <div className="mx-1 my-1">
            <Select
              value={filters.status}
              handleFilterChange={(value) => {
                handleFilterSelect(value, 'status');
              }}
              options={STATUS_OPTS}
              placeholder="Status"
              isClearable
              width="150px"
            />
          </div>
          <div className="mx-1 my-1">
            <InputFilter
              value={filters.pageName}
              handleFilterChange={(value) => {
                handleFilter(value, 'pageName');
              }}
              placeholder="Page Name"
              id="pageName"
              width="180px"
            />
          </div>
          <div className="mx-1 my-1">
            <InputFilter
              value={filters.businessProfileName}
              handleFilterChange={(value) => {
                handleFilter(value, 'businessProfileName');
              }}
              placeholder="Company Name"
              id="businessProfileName"
              width="180px"
            />
          </div>
          <div className="mx-1 my-1">
            <InputFilter
              value={filters.claimId}
              handleFilterChange={(value) => {
                handleFilter(value, 'claimId');
              }}
              placeholder="Claim Id"
              id="claimId"
              width="180px"
              inputType="number"
            />
          </div>
          <div className="mx-1 my-1">
            <div className="d-flex flex-row mx-2 justify-content-center align-items-center">
              <Typography variant="body1" className="mx-1">
                Sort By :
              </Typography>
              <Select
                value={filters.orderDir}
                handleFilterChange={(value) => {
                  handleFilterSelect(value, 'orderDir');
                  handleFilter('request_date', 'orderType');
                }}
                options={[
                  {
                    label: SortType.Oldest,
                    value: SortType.Oldest,
                  },
                  {
                    label: SortType.Latest,
                    value: SortType.Latest,
                  },
                ]}
                placeholder="request date"
                isClearable
                width="150px"
              />
            </div>
          </div>
        </div>

        <MaterialTable
          tableRef={tableRef}
          columns={[
            {
              title:     'Claim Id',
              field:     'id',
              cellStyle: {
                width:    '2%',
                maxWidth: '2%',
              },
              headerStyle: {
                width:    '2%',
                maxWidth: '2%',
              },
            },
            {
              title:     'Page Id',
              field:     'pageId',
              cellStyle: {
                width:    '2%',
                maxWidth: '2%',
              },
              headerStyle: {
                width:    '2%',
                maxWidth: '2%',
              },
            },
            {
              title: 'Page Name',
              field: 'pageName',
            },
            {
              title: 'Status',
              field: 'status',
            },
            {
              title: 'Company',
              field: 'businessProfileName',
            },
            {
              title: 'Type',
              field: 'pageType',
            },
            {
              title:  'Request Date',
              field:  'requestDate',
              render: (rowData) => {
                const dateObj = new Date(
                  rowData.requestDate.toLocaleString('en-US', {
                    timeZone: 'Asia/Singapore',
                  }),
                );
                return moment(dateObj).format('DD/MM/YYYY');
              },
            },
            {
              title: 'Remarks',
              field: 'remarks',
            },
          ]}
          // eslint-disable-next-line no-async-promise-executor
          data={(query) => new Promise(async (resolve) => {
            try {
              setIsLoading(true);
              const data = await apiService.getPageClaims({
                pageNo:   query.page,
                pageSize: query.pageSize,
                ...filters,
              });

              if (data) {
                resolve({
                  data:       data.rows || [],
                  page:       query.page || 0,
                  totalCount: data.totalCount || 0,
                });
                setIsLoading(false);
              } else {
                resolve({
                  data:       [],
                  page:       0,
                  totalCount: 0,
                });
              }
            } catch (err) {
              toast.error('Error In Table Filter');
              resolve({
                data:       [],
                page:       0,
                totalCount: 0,
              });
              setIsLoading(false);
            }
          })}
          actions={[
            {
              icon:    'assignment',
              tooltip: 'Check Details',
              onClick: (event, rowData) => {
                history.push(
                  `${Url.BusinessDetails.replace(':pageClaimId', rowData.id)}`,
                );
              },
            },
          ]}
          title="Page Claims"
          options={{
            pageSize,
            emptyRowsWhenPaging: false,
            actionsColumnIndex:  -1,
            search:              false,
            filtering:           false,
            minBodyHeight:       '400px',
            sorting:             false,
            debounceInterval:    500,
            pageSizeOptions:     [20, 40, 60, 80],
          }}
          onChangeRowsPerPage={(size) => setPageSize(size)}
          isLoading={isLoading}
          components={{ Container: (props) => <div {...props} /> }}
        />
      </Paper>

      <BusinessAccountsTable />
    </>
  );
}

export default BusinessClaimTable;
